<template>
  <ion-page>
    <ion-content class="h-full">
      <round-header
        image-type="emoji"
        image="🙏"
        :image-class="`transition-all duration-1000`"
        title="Gratitude Log"
        subtitle="Toolbox"
        back="/tools"
        disable-edit-mode="true"
      >
      </round-header>
      <div class="flex justify-center">
        <div class="w-full max-w-[1024px] p-4 px-8 md:px-20">
          <!-- Error message -->
          <div v-if="error" class="mb-4 rounded-lg bg-red-100 p-4 text-red-700">
            {{ error }}
          </div>

          <!-- Daily Prompt Section -->
          <div class="mb-6 text-center">
            <h3 class="font-fredoka font-outline-1 mt-2 mb-8 text-center text-3xl text-white">
              Go into yourself...
            </h3>
            <!-- Quick Entry Section -->

            <div class="flex gap-2">
              <input
                v-model="gratitudeText"
                placeholder="What are you grateful for today?"
                class="font-quicksand w-full resize-none rounded-xl border border-[#d8b99b] bg-[#fff7ef] p-3 text-base text-[#5c3d2e] shadow-inner focus:ring-2 focus:ring-[#caa37b] focus:outline-none"
              />
            </div>
            <div class="mb-4 rounded-xl bg-[#ffffff33] p-4">
              <div class="mb-4 flex flex-wrap justify-center gap-2">
                <button
                  v-for="emoji in Gratitude.getEmojiOptions()"
                  :key="emoji"
                  class="transform rounded-lg bg-[#ffffff55] p-2 text-2xl transition-all duration-200 hover:scale-110 hover:bg-[#ffffff77]"
                  :disabled="isLoading"
                  @click="quickAddGratitude(emoji)"
                >
                  {{ emoji }}
                </button>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="isLoading && gratitudes.length === 0" class="py-8 text-center">
            <div
              class="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
              role="status"
            >
              <span
                class="!absolute !-m-px !h-px !w-px !overflow-hidden !border-0 !p-0 !whitespace-nowrap ![clip:rect(0,0,0,0)]"
                >Loading...</span
              >
            </div>
            <p class="mt-2 text-white">Loading your gratitude journal...</p>
          </div>

          <!-- Gratitude Journal Grid -->
          <div v-else-if="gratitudes.length > 0">
            <h3 class="font-fredoka font-outline-1 mt-8 mb-8 text-center text-3xl text-white">
              Your Gratitude Journal
            </h3>

            <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
              <div
                v-for="gratitude in gratitudes"
                :key="gratitude.id"
                class="relative cursor-pointer overflow-hidden rounded-xl bg-[#ffffff44] p-4 transition-all duration-300 hover:bg-[#ffffff55] hover:shadow-lg"
                :class="{ 'animate-pulse': recentlyAdded === gratitude.id }"
                @click="showGlowEffect($event)"
              >
                <div class="flex items-center justify-between">
                  <div class="flex items-center">
                    <span class="mr-3 text-3xl">{{ gratitude.emoji }}</span>
                    <div>
                      <div class="break-words text-gray-700">
                        {{ gratitude.content }}
                      </div>
                      <div class="mt-1 text-sm text-gray-600">
                        {{ formatDate(gratitude.createdAt) }}
                      </div>
                    </div>
                  </div>
                  <button
                    :disabled="isLoading"
                    class="text-gray-500 transition-colors hover:text-red-500 disabled:cursor-not-allowed disabled:opacity-50"
                    title="Delete entry"
                    @click.stop="() => deleteGratitude(gratitude.id)"
                  >
                    <Trash2 class="h-5 w-5" />
                  </button>
                </div>

                <!-- Sparkle overlay (hidden by default) -->
                <div
                  class="sparkle-overlay pointer-events-none absolute inset-0 bg-gradient-to-r from-yellow-200 to-yellow-400 opacity-0"
                ></div>
              </div>
            </div>
          </div>

          <!-- Empty State -->
          <div v-else-if="!isLoading" class="mt-8 text-center text-gray-500">
            <div class="mb-2 text-4xl">✨</div>
            <p>Your gratitude journal is empty. Start adding things you're grateful for!</p>
          </div>
        </div>
      </div>
    </ion-content>
  </ion-page>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { IonContent, IonPage } from '@ionic/vue';
import RoundHeader from '../../components/RoundHeader.vue';
import { Trash2 } from 'lucide-vue-next';
import { Gratitude } from '../../models/Gratitude.ts';
import { collection, doc, setDoc, deleteDoc, getFirestore } from 'firebase/firestore';
import { useCurrentUser, useCollection } from 'vuefire';
import confetti from 'canvas-confetti';

// Firebase setup
const db = getFirestore();
const user = useCurrentUser();

// UI state
const gratitudeText = ref('');
const selectedEmoji = ref('❤️');
const dailyPrompt = ref(Gratitude.getDailyPrompt());
const error = ref<string | null>(null);
const recentlyAdded = ref<string | null>(null);

// Sound effects
const chimeSound = new Audio('/sounds/chimes.mp3');
if (chimeSound) {
  chimeSound.load(); // Preload to avoid delay
}

// Use VueFire's useCollection to reactively fetch gratitudes
const gratitudesCollection = useCollection(() =>
  user.value ? collection(db, `users/${user.value.uid}/gratitudes`) : null
);

// Transform the raw Firestore data into Gratitude objects and sort by creation date
const gratitudes = computed(() => {
  if (!gratitudesCollection.value) return [];

  return gratitudesCollection.value
    .map((data) => {
      const gratitude = new Gratitude();
      Object.assign(gratitude, data);
      return gratitude;
    })
    .sort((a, b) => b.createdAt - a.createdAt);
});

// Loading state is handled by VueFire
const isLoading = computed(() => !user.value || gratitudesCollection.value === undefined);

// Format date to a readable string
function formatDate(timestamp: number): string {
  const date = new Date(timestamp);
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === now.toDateString()) {
    return `Today at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  } else if (date.toDateString() === yesterday.toDateString()) {
    return `Yesterday at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  } else {
    return (
      date.toLocaleDateString([], { month: 'short', day: 'numeric' }) +
      ` at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
    );
  }
}

// Quick add a gratitude with just an emoji
async function quickAddGratitude(emoji: string) {
  selectedEmoji.value = emoji;
  await addGratitude();
}

// Add a new gratitude entry
async function addGratitude(emojiOnly = false) {
  if (!user.value) {
    error.value = 'You must be logged in to save entries';
    return;
  }

  if (!emojiOnly && !gratitudeText.value.trim()) {
    return;
  }

  error.value = null;

  try {
    const gratitude = new Gratitude();
    gratitude.emoji = selectedEmoji.value;

    if (emojiOnly) {
      // Just use the emoji as content
      gratitude.content = selectedEmoji.value;
    } else {
      gratitude.content = gratitudeText.value.trim();
    }

    const gratitudeRef = doc(db, `users/${user.value.uid}/gratitudes/${gratitude.id}`);
    await setDoc(gratitudeRef, gratitude.toPlain());

    // Play sound effect
    if (chimeSound) {
      chimeSound.currentTime = 0;
      chimeSound.play().catch((err) => console.log('Audio play error:', err));

      // Show confetti after a 1-second delay to align with the sound's high point
      setTimeout(() => {
        confetti({
          particleCount: 50,
          spread: 70,
          origin: { y: 0.6 },
        });
      }, 1000); // 1 second delay
    } else {
      // Fallback if sound doesn't load - show confetti immediately
      confetti({
        particleCount: 50,
        spread: 70,
        origin: { y: 0.6 },
      });
    }

    // Mark as recently added for animation
    recentlyAdded.value = gratitude.id;
    setTimeout(() => {
      recentlyAdded.value = null;
    }, 2000);

    // Reset form
    gratitudeText.value = '';

    // Generate a new prompt occasionally
    if (Math.random() > 0.7) {
      dailyPrompt.value = Gratitude.getDailyPrompt();
    }
  } catch (err) {
    console.error('Error saving gratitude:', err);
    error.value = 'Failed to save entry. Please try again.';
  }
}

// Delete a gratitude entry
async function deleteGratitude(id: string) {
  if (!user.value) {
    error.value = 'You must be logged in to delete entries';
    return;
  }

  error.value = null;

  try {
    const gratitudeRef = doc(db, `users/${user.value.uid}/gratitudes/${id}`);
    await deleteDoc(gratitudeRef);
  } catch (err) {
    console.error('Error deleting gratitude:', err);
    error.value = 'Failed to delete entry. Please try again.';
  }
}

// Show glow effect when clicking on a gratitude card
function showGlowEffect(event: MouseEvent) {
  const target = event.currentTarget as HTMLElement;
  const overlay = target.querySelector('.sparkle-overlay') as HTMLElement;

  if (overlay) {
    // Show the overlay
    overlay.style.opacity = '0.6';

    // Add a small sparkle effect
    confetti({
      particleCount: 15,
      spread: 40,
      origin: {
        x: (event.clientX - target.getBoundingClientRect().left) / target.offsetWidth,
        y: (event.clientY - target.getBoundingClientRect().top) / target.offsetHeight,
      },
    });

    // Hide the overlay after a short delay
    setTimeout(() => {
      overlay.style.opacity = '0';
    }, 300);
  }
}

// Generate a new prompt when the component is mounted
onMounted(() => {
  dailyPrompt.value = Gratitude.getDailyPrompt();

  // Preload the chime sound
  if (chimeSound) {
    chimeSound.load();
  }
});
</script>

<style scoped>
.sparkle-overlay {
  transition: opacity 0.3s ease;
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
}

.animate-pulse {
  animation: pulse 1s ease-in-out infinite;
}
</style>
